import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import {
  AuthSelectors,
  BaseAppState,
  ENVIRONMENT,
  Environment,
  getIsRelativeUserAccount,
  goToSurvey,
  ModalsActions,
  OptionalApplicationFeature,
  RelativeAccountActions,
  SurveyApiActions,
  SurveySelectors,
  UsersActions,
  UsersSelectors,
  WeightHistoryApiActions,
  WeightHistorySelectors,
  MAIN_NAVIGATION_PATHS,
  Survey,
} from 'ngx-esprio-shared';
import { BehaviorSubject, combineLatest, filter, map, take } from 'rxjs';
import { environment } from 'projects/webk/src/environments/environment';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard',
  templateUrl: 'dashboard.component.html',
  styleUrls: ['dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  @ViewChild('webkSection') webkSection!: ElementRef<HTMLDivElement>;

  public userDetails$ = this.store.select(UsersSelectors.getUserDetails);
  public userId$ = this.userDetails$.pipe(map((user) => user?.id ?? null));
  public weightData$ = this.store.select(WeightHistorySelectors.getData);
  public basicWebkData$ = this.store.select(SurveySelectors.getBasicWebkHistoryData);
  public weightChangePercentage$ = this.store.select(
    WeightHistorySelectors.getWeightChangePercentage
  );
  public timeSnap$ = this.store.select(WeightHistorySelectors.getTimeSnap);
  public scoreDetails$ = this.store.select(SurveySelectors.getUserScoreDetails);
  public dataAvailability$ = this.store.select(SurveySelectors.getDataAvailability);
  public isRelativeAccount$ = this.store.select(getIsRelativeUserAccount);
  public getLoggedInUserId$ = this.store.select(AuthSelectors.getLoggedInUserId);
  public submissions$ = this.store.select(SurveySelectors.getSubmissions);
  public latestSubmissionDetails$ = this.store.select(SurveySelectors.getLatestSubmissionDetails);
  public surveys$ = this.store.select(SurveySelectors.getSurveys);
  public showSuccessMessage = false;

  public firstAvailableSurvey$ = combineLatest([
    this.surveys$,
    this.store.select(SurveySelectors.getSubmissionsExist),
  ]).pipe(
    map(([surveys]) => {
      const survey = surveys.find(
        (s) => s.familyName !== 'on-demand-baseline' && s.familyName !== 'on-demand-baselineWebk'
      );
      if (!survey) return null;

      return {
        ...survey,
        action:
          survey.continuedSubmissionId && this.environment !== Environment.webk
            ? this.translateService.instant('dashboard.continue_survey')
            : this.translateService.instant('dashboard.new_survey'),
      };
    })
  );

  public readonly asyncProcessKeys = [
    SurveyApiActions.createSubmission.type,
    SurveyApiActions.getBasicWebkHistoryData.type,
    WeightHistoryApiActions.getWeightHistoryData.type,
    SurveyApiActions.checkDataAvailability.type,
    RelativeAccountActions.getRelativePatients.type,
  ];

  public Environment = Environment;
  public nutritionDiaryDisabled: boolean;
  public date$ = new BehaviorSubject<Date>(moment().startOf('month').toDate());
  public date = moment().startOf('month').toDate();
  shownIndex = 0;

  constructor(
    @Inject(ENVIRONMENT) public environment: Environment,
    private store: Store<BaseAppState>,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.fetchChartData();
    this.loadPatientsData();
    this.store.dispatch(SurveyApiActions.checkDataAvailability());
    this.fetchUserDetails();
    this.store.dispatch(SurveyApiActions.getSurveys());

    this.getLoggedInUserId$.pipe(take(1)).subscribe((userId) => {
      if (userId) {
        this.store.dispatch(WeightHistoryApiActions.getTimeSnap({ userId }));
        this.store.dispatch(SurveyApiActions.getSubmissions({ userId }));
        this.store.dispatch(SurveyApiActions.getLatestSubmissionDetails({ userId }));
      }
    });

    this.nutritionDiaryDisabled = environment.disabledAppFeatures.includes(
      OptionalApplicationFeature.NutritionDiary
    );
  }

  ngAfterViewInit(): void {
    // WebK (ticket 27756): daily weight + survey prompt chain (see ModalsEffects).
    this.store.dispatch(ModalsActions.openDashboardPromptsIfNeeded());
    this.store.dispatch(SurveyApiActions.getBasicWebkHistoryData());
  }

  public bmi$ = combineLatest([this.timeSnap$, this.userDetails$]).pipe(
    filter(([timeSnap, user]) => !!timeSnap && !!user?.coreData?.bodySizeInCm),
    map(([timeSnap, user]) => {
      const heightInM = user!.coreData!.bodySizeInCm / 100;
      return Math.floor(timeSnap!.weight / (heightInM * heightInM));
    })
  );

  public vm$ = combineLatest([
    this.userDetails$,
    this.bmi$,
    this.weightChangePercentage$,
    this.timeSnap$,
    this.scoreDetails$,
    this.latestSubmissionDetails$,
  ]).pipe(
    map(([user, bmi, weightChange, timeSnap, scoreDetails, latestSubmissionDetails]) => ({
      user,
      bmi,
      weightChangePercentage: weightChange?.value ?? null,
      timeSnap,
      scoreDetails,
      latestSubmissionDetails,
    }))
  );

  handleDateChange(date: Date): void {
    this.date$.next(date);
    this.fetchChartData();
  }

  handleOpenWeightModalClick(): void {
    this.store.dispatch(ModalsActions.OpenAddWeightDataModal());
  }

  handleNewSurveyClick(): void {
    this.store.dispatch(SurveyApiActions.createSubmission());
  }

  handleGoToSurveyAnalysis(submissionId: string): void {
    this.store.dispatch(goToSurvey({ submissionId }));
  }

  handleHelpClicked(): void {
    this.router.navigate([MAIN_NAVIGATION_PATHS.root.consultation]);
  }

  handleStartSurveyClick(survey: Survey & { action: string }): void {
    if (!survey.continuedSubmissionId) {
      this.store.dispatch(
        SurveyApiActions.createSubmission({
          scheduleId: survey.scheduleId,
          surveyId: survey.id,
        })
      );
    } else {
      this.store.dispatch(goToSurvey({ submissionId: survey.continuedSubmissionId }));
    }
  }

  private fetchChartData(): void {
    const selectedDate = this.date$.value;
    const startDate = moment(selectedDate).startOf('month').toDate();
    const endDate = moment(selectedDate).endOf('month').toDate();

    this.store.dispatch(WeightHistoryApiActions.getWeightHistoryData({ startDate, endDate }));
    this.store.dispatch(SurveyApiActions.getBasicWebkHistoryData({ startDate, endDate }));
  }

  private fetchUserDetails(): void {
    this.getLoggedInUserId$.pipe(take(1)).subscribe((userId) => {
      if (userId) {
        this.store.dispatch(UsersActions.getUserDetails({ userId }));
        this.store.dispatch(SurveyApiActions.getUserScoreDetails({ userId }));

        const startDate = moment().subtract(30, 'days').toDate();
        const endDate = moment().toDate();
        this.store.dispatch(
          WeightHistoryApiActions.getWeightChangePercentage({ startDate, endDate })
        );
      }
    });
  }

  private loadPatientsData(): void {
    this.isRelativeAccount$.pipe(take(1)).subscribe((isRelativeAccount) => {
      if (isRelativeAccount) {
        this.store.dispatch(RelativeAccountActions.getRelativePatients());
      }
    });
  }
}
